import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { cn } from '../../lib/utils';
import { MessageSquare, FileText, Users, Clock, Mic } from 'lucide-react';
import { useAppMode } from '../../contexts/AppModeContext';

const DEMO_SESSIONS = [
  { id: 'DIC-001', patient: 'John Smith', patientId: 'P001', date: '2026-02-03', duration: '4:12', status: 'completed', noteGenerated: true, credits: 5 },
  { id: 'DIC-002', patient: 'Maria Garcia', patientId: 'P002', date: '2026-02-03', duration: '3:45', status: 'completed', noteGenerated: true, credits: 4 },
  { id: 'DIC-003', patient: 'Emily Davis', patientId: 'P004', date: '2026-02-02', duration: '5:30', status: 'completed', noteGenerated: false, credits: 6 },
  { id: 'DIC-004', patient: 'Sarah Williams', patientId: 'P005', date: '2026-02-01', duration: '7:15', status: 'completed', noteGenerated: true, credits: 8 },
];

const LuminaDictation = () => {
  const { isDemo } = useAppMode();
  const navigate = useNavigate();

  return (
    <div className="space-y-6" data-testid="dictation-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold font-['Plus_Jakarta_Sans'] text-slate-900">Dictation</h1>
          <p className="text-slate-500 mt-1">Push-to-record dictation with real-time transcription &middot; 1 credit/min</p>
        </div>
        <Button onClick={() => navigate('/lumina/patients')} className="bg-blue-600 hover:bg-blue-700 gap-2" data-testid="new-dictation-session-btn">
          <MessageSquare className="h-4 w-4" /> New Session
        </Button>
      </div>

      {/* Info Card */}
      <Card className="border-blue-200 bg-blue-50/40">
        <CardContent className="p-4 flex items-center gap-4">
          <div className="h-10 w-10 rounded-xl bg-blue-100 flex items-center justify-center shrink-0">
            <MessageSquare className="h-5 w-5 text-blue-600" />
          </div>
          <div>
            <p className="text-sm font-medium text-slate-800">Start dictation sessions from any patient chart</p>
            <p className="text-xs text-muted-foreground">Open a patient's chart and click "Dictation" to begin. Hold the mic button to record, release to pause. The session stays within the chart.</p>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Session History */}
        <div className="lg:col-span-2">
          <Card>
            <CardHeader>
              <CardTitle className="text-base font-semibold">Dictation History</CardTitle>
              <CardDescription>{DEMO_SESSIONS.length} recordings</CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              {DEMO_SESSIONS.map((session) => (
                <div
                  key={session.id}
                  className="flex items-center justify-between p-4 rounded-xl border hover:bg-slate-50 cursor-pointer transition-colors"
                  onClick={() => navigate(`/lumina/patients/${session.patientId}`)}
                  data-testid={`dictation-session-${session.id}`}
                >
                  <div className="flex items-center gap-4">
                    <div className="h-10 w-10 rounded-full bg-blue-50 flex items-center justify-center">
                      <MessageSquare className="h-5 w-5 text-blue-500" />
                    </div>
                    <div>
                      <p className="text-sm font-medium">{session.patient}</p>
                      <p className="text-xs text-muted-foreground">{session.date} &middot; {session.duration}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-muted-foreground">{session.credits} credits</span>
                    {session.noteGenerated ? (
                      <Badge className="bg-blue-50 text-blue-700 text-xs gap-1"><FileText className="h-3 w-3" /> Note Generated</Badge>
                    ) : (
                      <Button size="sm" variant="outline" className="h-7 text-xs gap-1" onClick={(e) => { e.stopPropagation(); navigate('/lumina/soap-notes'); }}>
                        <FileText className="h-3 w-3" /> Generate Note
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>

        {/* Right Panel */}
        <div className="space-y-4">
          <Card>
            <CardHeader className="pb-3"><CardTitle className="text-sm font-semibold">How It Works</CardTitle></CardHeader>
            <CardContent className="space-y-3 text-xs text-muted-foreground">
              <div className="flex items-start gap-2">
                <div className="h-5 w-5 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-[10px] font-bold shrink-0 mt-0.5">1</div>
                <p>Open a patient chart and start a dictation session</p>
              </div>
              <div className="flex items-start gap-2">
                <div className="h-5 w-5 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-[10px] font-bold shrink-0 mt-0.5">2</div>
                <p>Hold the mic button to record, release to pause</p>
              </div>
              <div className="flex items-start gap-2">
                <div className="h-5 w-5 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-[10px] font-bold shrink-0 mt-0.5">3</div>
                <p>AI structures your dictation into a SOAP note</p>
              </div>
              <div className="flex items-start gap-2">
                <div className="h-5 w-5 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-[10px] font-bold shrink-0 mt-0.5">4</div>
                <p>Review, edit, and copy to your EHR</p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3"><CardTitle className="text-sm font-semibold">This Week</CardTitle></CardHeader>
            <CardContent className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-muted-foreground">Sessions</span><span className="font-medium">{DEMO_SESSIONS.length}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Total Time</span><span className="font-medium">20 min</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Credits Used</span><span className="font-medium">23</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Notes Generated</span><span className="font-medium">{DEMO_SESSIONS.filter(s => s.noteGenerated).length}</span></div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default LuminaDictation;
